import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import AssignmentIcon from "@mui/icons-material/Assignment";
import BookIcon from "@mui/icons-material/Book";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { RootState } from "../../../../../store/Index";
import { setSelectedBook } from "../../../../../store/slice/MemoryBookSlice";
import StatsCard from "../../../../bookings/list-booking2/StatsCard";
import MemoryBookLoginModal from "./MemoryBookLoginModal";
import MemoryBookUpdateModal from "./MemoryBookUpdateModal";
import {
  fetchAssignedMemoryBooks,
  roleIncludesOnline,
  roleIncludesPhysical,
} from "./service/memoryBook.api";
import MemoryBookAssignmentsTabs from "./MemoryBookAssignments";

const MemoryBookDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const { selectedBook, dailyGoal } = useSelector(
    (state: RootState) => state.memoryBook,
  );
  const user = useSelector((state: RootState) => state.auth.user);

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    const state = location.state as { showModal?: boolean };
    if (state?.showModal) {
      setShowLoginModal(true);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    const validateAssignment = async () => {
      if (!user?.id || !selectedBook) return;

      try {
        const res = await fetchAssignedMemoryBooks(user.id);
        const results = res.data?.results ?? [];

        const assignment = results.find(
          (item) => String(item.stream?.id) === selectedBook.id,
        );

        if (!assignment) {
          dispatch(setSelectedBook(null));
          setShowLoginModal(true);
          return;
        }
        const physicalComments = assignment.memory_book?.physical_comments ?? 0;
        const donePhysical = assignment.memory_book?.done_comments ?? 0;
        const unfinishedPhysical = Math.max(physicalComments - donePhysical, 0);

        const onlineComments = assignment.online_comments || 0;
        const unfinishedOnline =
          assignment.online_remaining_comments ?? onlineComments;
        const doneOnline = onlineComments - unfinishedOnline;

        const totalUnfinished = unfinishedPhysical + unfinishedOnline;

        const deceasedName = assignment.stream?.booking?.deceased?.[0]
          ? `${assignment.stream.booking.deceased[0].first_name} ${assignment.stream.booking.deceased[0].surname}`
          : "Unnamed Book";

        const streamTitle = assignment.stream?.title || "Untitled Stream";
        const deceasedPhoto =
          assignment.stream?.booking?.deceased?.[0]?.person_image;

        dispatch(
          setSelectedBook({
            id: String(assignment.stream?.id),
            assignmentId: assignment.id,
            title: streamTitle || deceasedName,
            thumbnail: deceasedPhoto,
            physical_comments: physicalComments,
            unfinished_comments: unfinishedPhysical,
            online_comments: onlineComments,
            unfinished_online_comments: unfinishedOnline,
            total_comments: physicalComments + onlineComments,
            dueDate: assignment.due_date,
            inDanger: assignment.in_danger,
            status:
              totalUnfinished === 0
                ? "completed"
                : donePhysical > 0 || doneOnline > 0
                  ? "in_progress"
                  : "not_started",
          }),
        );
      } catch {
        toast.error("Failed to check assignment");
      }
    };

    validateAssignment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedBookId = selectedBook?.id;

  const handleUpdateSuccess = useCallback(async () => {
    if (!selectedBookId || !user?.id) return;

    try {
      const res = await fetchAssignedMemoryBooks(user.id);
      const results = res.data?.results ?? [];

      const assignment = results.find(
        (item) => String(item.stream?.id) === selectedBookId,
      );

      if (!assignment) {
        dispatch(setSelectedBook(null));
        setShowLoginModal(true);
        return;
      }

      const physicalComments = assignment.memory_book?.physical_comments ?? 0;
      const donePhysical = assignment.memory_book?.done_comments ?? 0;
      const unfinishedPhysical = Math.max(physicalComments - donePhysical, 0);

      const onlineComments = assignment.online_comments || 0;
      const unfinishedOnline =
        assignment.online_remaining_comments ?? onlineComments;
      const doneOnline = onlineComments - unfinishedOnline;

      const totalUnfinished = unfinishedPhysical + unfinishedOnline;

      const deceasedName = assignment.stream?.booking?.deceased?.[0]
        ? `${assignment.stream.booking.deceased[0].first_name} ${assignment.stream.booking.deceased[0].surname}`
        : "Unnamed Book";

      const streamTitle = assignment.stream?.title || "Untitled Stream";
      const deceasedPhoto =
        assignment.stream?.booking?.deceased?.[0]?.person_image;

      dispatch(
        setSelectedBook({
          id: String(assignment.stream?.id),
          assignmentId: assignment.id,
          title: streamTitle || deceasedName,
          thumbnail: deceasedPhoto,
          physical_comments: physicalComments,
          unfinished_comments: unfinishedPhysical,
          online_comments: onlineComments,
          unfinished_online_comments: unfinishedOnline,
          total_comments: physicalComments + onlineComments,
          dueDate: assignment.due_date,
          inDanger: assignment.in_danger,
          role: assignment.role ?? null,
          status:
            totalUnfinished === 0
              ? "completed"
              : donePhysical > 0 || doneOnline > 0
                ? "in_progress"
                : "not_started",
        }),
      );

      setShowUpdateModal(false);
    } catch (error) {
      console.error("Failed to refresh book data:", error);
      toast.error("Failed to refresh book data");
    }
  }, [selectedBookId, user?.id, dispatch]);

  const handleUpdateScanned = () => {
    setShowUpdateModal(true);
  };

  const showPhysical =
    roleIncludesPhysical(selectedBook?.role) &&
    (selectedBook?.physical_comments ?? 0) > 0;
  const showOnline =
    roleIncludesOnline(selectedBook?.role) &&
    (selectedBook?.online_comments ?? 0) > 0;

  const isCompleted = selectedBook
    ? (!showPhysical || selectedBook.unfinished_comments === 0) &&
      (!showOnline || selectedBook.unfinished_online_comments === 0)
    : false;

  const updateButtonLabel = isCompleted
    ? "All Comments Scanned"
    : showPhysical && showOnline
      ? "Update Scanned/Uploaded Comments"
      : showOnline
        ? "Update Uploaded Comments"
        : "Update Scanned Comments";

  if (!selectedBook) {
    return (
      <>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: "center",
            maxWidth: "100%",
            mx: "auto",
            mt: 8,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            No Book Selected
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {
              "You haven't selected a memory book yet. Please choose one to start working."
            }
          </Typography>
          <Button
            variant="contained"
            onClick={() => setShowLoginModal(true)}
            sx={{
              textTransform: "none",
              borderRadius: "12px",
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${
                reduxTheme.primary_colors?.[500] || "#503629"
              } 0%, ${reduxTheme.primary_colors?.[400] || "#614438"} 100%)`,
            }}
          >
            Select a Memory Book
          </Button>
        </Paper>
        <MemoryBookLoginModal
          open={showLoginModal}
          onClose={() => setShowLoginModal(false)}
        />
      </>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
            }}
          >
            Welcome back, {user?.first_name}!
          </Typography>
        </Box>

        <Button
          variant="outlined"
          onClick={() => setShowLoginModal(true)}
          sx={{
            textTransform: "none",
            borderRadius: "12px",
            px: 3,
            py: 1.5,
            borderColor: reduxTheme.primary_colors?.[400] || "#614438",
            color: reduxTheme.primary_colors?.[400] || "#614438",
            "&:hover": {
              borderColor: reduxTheme.primary_colors?.[500] || "#503629",
              backgroundColor: `${reduxTheme.primary_colors?.[400]}10`,
            },
          }}
        >
          Switch Book
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 4, flexWrap: "wrap" }}>
        <StatsCard
          title="Today's Goal"
          value={dailyGoal}
          icon={<AssignmentIcon />}
          backgroundColor="#4BA663"
          subtitle="Comments to scan and upload today"
        />
          <StatsCard
            title="Physical Remaining"
            value={selectedBook?.unfinished_comments ?? 0}
            icon={<BookIcon />}
            backgroundColor="#FFA500"
            subtitle="Comments to scan"
          />
          <StatsCard
            title="Physical Done"
            value={
              selectedBook
                ? selectedBook.physical_comments -
                  selectedBook.unfinished_comments
                : 0
            }
            icon={<TrendingUpIcon />}
            backgroundColor="#4BA663"
            subtitle="Comments scanned"
          />
          <StatsCard
            title="Online Remaining"
            value={selectedBook?.unfinished_online_comments ?? 0}
            icon={<TrendingUpIcon />}
            backgroundColor="#007AFF"
            subtitle="Comments to upload"
          />
        <StatsCard
          title="Online Done"
          value={
            selectedBook
              ? selectedBook.online_comments -
                selectedBook.unfinished_online_comments
              : 0
          }
            icon={<TrendingUpIcon />}
            backgroundColor="#9C27B0"
            subtitle="Comments uploaded"
          />

      </Box>

      {/* Current Book Progress */}
      {selectedBook && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            boxShadow: "none",
            mb: 4,
            position: "relative",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {selectedBook.title}
            </Typography>
            <Chip
              label={isCompleted ? "Completed" : "In Progress"}
              size="small"
              sx={{
                fontWeight: 600,
                backgroundColor: isCompleted ? "#4BA66320" : "#FFA50020",
                color: isCompleted ? "#4BA663" : "#FFA500",
                border: `1px solid ${isCompleted ? "#4BA663" : "#FFA500"}`,
              }}
            />
          </Box>

          {/* Physical */}
          {showPhysical && (
          <Box mb={2.5}>
            <Box display="flex" justifyContent="space-between" mb={0.75}>
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={600}
              >
                Physical Scanned
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {selectedBook.physical_comments -
                  selectedBook.unfinished_comments}{" "}
                / {selectedBook.physical_comments}
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 0.5 }}
                >
                  (
                  {selectedBook.physical_comments > 0
                    ? (
                        ((selectedBook.physical_comments -
                          selectedBook.unfinished_comments) /
                          selectedBook.physical_comments) *
                        100
                      ).toFixed(1)
                    : 0}
                  %)
                </Typography>
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={
                selectedBook.physical_comments > 0
                  ? ((selectedBook.physical_comments -
                      selectedBook.unfinished_comments) /
                      selectedBook.physical_comments) *
                    100
                  : 0
              }
              sx={{
                height: 8,
                borderRadius: 2,
                backgroundColor: reduxTheme.neutral_colors?.[800],
                "& .MuiLinearProgress-bar": {
                  borderRadius: 2,
                  background: `linear-gradient(90deg, ${reduxTheme.primary_colors?.[400]} 0%, ${reduxTheme.primary_colors?.[500]} 100%)`,
                },
              }}
            />
          </Box>
          )}

          {/* Online */}
          {showOnline && (
            <Box mb={3}>
              <Box display="flex" justifyContent="space-between" mb={0.75}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={600}
                >
                  Online Uploaded
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {selectedBook.online_comments -
                    selectedBook.unfinished_online_comments}{" "}
                  / {selectedBook.online_comments}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 0.5 }}
                  >
                    (
                    {(
                      ((selectedBook.online_comments -
                        selectedBook.unfinished_online_comments) /
                        selectedBook.online_comments) *
                      100
                    ).toFixed(1)}
                    %)
                  </Typography>
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={
                  ((selectedBook.online_comments -
                    selectedBook.unfinished_online_comments) /
                    selectedBook.online_comments) *
                  100
                }
                sx={{
                  height: 8,
                  borderRadius: 2,
                  backgroundColor: reduxTheme.neutral_colors?.[800],
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 2,
                    background: `linear-gradient(90deg, ${reduxTheme.primary_colors?.[500]} 0%, ${reduxTheme.primary_colors?.[600]} 100%)`,
                  },
                }}
              />
            </Box>
          )}

          <Button
            variant="contained"
            fullWidth
            component={motion.button}
            onClick={handleUpdateScanned}
            disabled={isCompleted}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              py: 1.2,
              background: `linear-gradient(135deg, ${reduxTheme.primary_colors?.[400]} 0%, ${reduxTheme.primary_colors?.[500]} 100%)`,
              "&:disabled": {
                opacity: 0.4,
              },
            }}
          >
            {updateButtonLabel}
          </Button>
        </Paper>
      )}
      <Box mt={3}>
        <MemoryBookAssignmentsTabs
          onSwitchBook={() => setShowUpdateModal(false)}
        />
      </Box>
      <MemoryBookLoginModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
      <MemoryBookUpdateModal
        open={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        onSuccess={handleUpdateSuccess}
      />
    </Box>
  );
};

export default MemoryBookDashboard;
